import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import clientSiteService from '../../services/clientSiteService';
import clientService from '../../services/clientService';
import NotificationToast from '../../components/ui/NotificationToast';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import AddClientSiteModal from './components/AddClientSiteModal';

const ClientSiteManagement = () => {
  const { userProfile } = useAuth();
  const { isCollapsed } = useSidebar();
  const [searchParams] = useSearchParams();
  const clientIdFilter = searchParams.get('client_id');
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClient, setFilterClient] = useState(clientIdFilter || 'all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [notification, setNotification] = useState(null);

  const user = {
    name: userProfile?.full_name || "Admin",
    email: userProfile?.email || "admin@facilitypro.com",
    role: userProfile?.role || "Admin"
  };

  useEffect(() => {
    loadClients();
    loadSites();
  }, [searchTerm, filterClient, filterStatus]);

  const loadClients = async () => {
    try {
      const result = await clientService.getClients({ status: 'active' });
      if (result?.success) {
        setClients(result.data || []);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadSites = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (filterClient !== 'all') filters.client_id = filterClient;
      if (filterStatus !== 'all') filters.is_active = filterStatus === 'active' ? 'true' : 'false';
      
      const result = await clientSiteService.getClientSites(filters);
      if (result?.success) {
        setSites(result.data || []);
      } else {
        setNotification({ message: result?.error || 'Failed to load client sites', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading client sites:', error);
      setNotification({ message: 'An error occurred while loading client sites', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSite = () => {
    setEditingSite(null);
    setShowAddModal(true);
  };

  const handleEditSite = (site) => {
    setEditingSite(site);
    setShowAddModal(true);
  };

  const handleDeleteSite = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client site? This will also remove all associated geo-fences and deployment records.')) return;
    
    try {
      const result = await clientSiteService.deleteClientSite(id);
      if (result?.success) {
        setNotification({ message: 'Client site deleted successfully', type: 'success' });
        loadSites();
      } else {
        setNotification({ message: result?.error || 'Failed to delete client site', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleSiteSaved = () => {
    setShowAddModal(false);
    setEditingSite(null);
    loadSites();
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || 'Unknown Client';
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        notifications={[]}
      />
      
      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          <BreadcrumbNavigation />
          
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Client Site Management</h1>
              <p className="text-muted-foreground mt-2">
                Manage client sites where employees are deployed
              </p>
            </div>
            <Button onClick={handleAddSite} iconName="Plus" iconPosition="left">
              Add Client Site
            </Button>
          </div>

          <NotificationToast
            message={notification?.message}
            type={notification?.type}
            onDismiss={() => setNotification(null)}
          />

          {/* Filters */}
          <div className="mb-6 flex gap-4 items-center flex-wrap">
            <div className="flex-1 max-w-md">
              <Input
                placeholder="Search sites..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                iconName="Search"
              />
            </div>
            <select
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="all">All Clients</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.company_name}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Sites Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading client sites...</p>
            </div>
          ) : sites.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-lg">
              <Icon name="MapPin" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
              <p className="text-muted-foreground">No client sites found</p>
              <p className="text-sm text-muted-foreground mt-2">
                {searchTerm || filterClient !== 'all' ? 'Try adjusting your filters' : 'Get started by adding your first client site'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sites.map((site) => (
                <div
                  key={site.id}
                  className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <Icon name="MapPin" size={24} className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{site.site_name}</h3>
                        <p className="text-xs text-muted-foreground">{getClientName(site.client_id)}</p>
                        <span className={`text-xs px-2 py-0.5 rounded mt-1 inline-block ${
                          site.is_active 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                        }`}>
                          {site.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditSite(site)}
                        title="Edit"
                      >
                        <Icon name="Edit" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSite(site.id)}
                        className="text-destructive"
                        title="Delete"
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {site.address && (
                      <div className="flex items-start gap-2 text-muted-foreground">
                        <Icon name="MapPin" size={14} className="mt-0.5" />
                        <span className="line-clamp-2">{site.address}</span>
                      </div>
                    )}
                    {(site.city || site.state) && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="Building" size={14} />
                        <span>{[site.city, site.state, site.pincode].filter(Boolean).join(', ')}</span>
                      </div>
                    )}
                    {site.contact_person && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="User" size={14} />
                        <span>{site.contact_person}</span>
                      </div>
                    )}
                    {site.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="Phone" size={14} />
                        <span>{site.phone}</span>
                      </div>
                    )}
                    {(site.latitude && site.longitude) && (
                      <div className="flex items-center gap-2 text-muted-foreground text-xs">
                        <Icon name="Navigation" size={14} />
                        <span>{parseFloat(site.latitude).toFixed(6)}, {parseFloat(site.longitude).toFixed(6)}</span>
                      </div>
                    )}
                    {site.active_deployments !== undefined && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Icon name="Users" size={14} />
                        <span>{site.active_deployments} active deployment(s)</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-border flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => window.location.href = `/employee-deployments?client_site_id=${site.id}`}
                    >
                      <Icon name="Users" size={14} className="mr-2" />
                      Deployments
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => window.location.href = `/employee-management?tab=geo-fences&client_site_id=${site.id}`}
                    >
                      <Icon name="MapPin" size={14} className="mr-2" />
                      Geo-fences
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Client Site Modal */}
      <AddClientSiteModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingSite(null);
        }}
        site={editingSite}
        clients={clients}
        onSave={handleSiteSaved}
      />
    </div>
  );
};

export default ClientSiteManagement;

